import type { ReactNode } from 'react';
import type { ResumeData, ResumeSettings } from '../types/resume';
import { mergeSettings, formatDate } from '../lib/resume-settings';

type TemplateProps = { data: ResumeData; settings?: ResumeSettings };
type SectionProps = { title: string; children: ReactNode; className?: string };

function Description({ text, className = 'text-[11px] leading-4 text-slate-700', bScale = 1 }: { text: string; className?: string; bScale?: number }) {
  if (!text) return null;
  const lines = text.split('\n').map((line) => line.trim()).filter(Boolean);
  if (lines.length < 2) return <p className={`${className} whitespace-pre-line`} style={{ fontSize: `${0.6875 * bScale}rem` }}>{text}</p>;
  return (
    <ul className={`${className} list-disc space-y-0.5 pl-4`} style={{ fontSize: `${0.6875 * bScale}rem` }}>
      {lines.map((line, index) => <li key={index}>{line.replace(/^[-*\s]+/, '')}</li>)}
    </ul>
  );
}

function ContactLine({ data, className = 'text-[10px]', bScale = 1 }: { data: ResumeData; className?: string; bScale?: number }) {
  const { email, phone, location, website, linkedin } = data.personal;
  const contact = [email, phone, location, website, linkedin].filter(Boolean);
  return contact.length ? <p className={className} style={{ fontSize: `${0.625 * bScale}rem` }}>{contact.join(' | ')}</p> : null;
}

function Section({ title, children, className = '' }: SectionProps) {
  return (
    <section className={className}>
      <h2 className="mb-2 border-b border-slate-400 pb-1 text-[11px] font-bold uppercase tracking-[0.14em]">{title}</h2>
      {children}
    </section>
  );
}

function ExperienceList({ data, mode = 'plain', bScale = 1 }: { data: ResumeData; mode?: 'plain' | 'boxed' | 'timeline' | 'numbered'; bScale?: number }) {
  return (
    <div className={mode === 'timeline' ? 'space-y-0' : 'space-y-3'}>
      {data.experience.map((item, index) => (
        <article
          key={item.id}
          className={
            mode === 'boxed' ? 'border border-slate-200 p-3' :
            mode === 'timeline' ? 'relative border-l-2 border-sky-300 pb-4 pl-5 last:pb-0' :
            mode === 'numbered' ? 'grid grid-cols-[28px_1fr] gap-2 border-b border-slate-200 pb-3 last:border-0' : ''
          }
        >
          {mode === 'timeline' && <span className="absolute -left-[5px] top-1 h-2 w-2 rounded-full bg-sky-600" />}
          {mode === 'numbered' && <span className="font-mono text-xs font-bold text-slate-400">{String(index + 1).padStart(2, '0')}</span>}
          <div>
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="text-xs font-bold text-slate-900" style={{ fontSize: `${0.75 * bScale}rem` }}>{item.title}</h3>
              <span className="shrink-0 text-slate-500" style={{ fontSize: `${0.625 * bScale}rem` }}>
                {formatDate(item.startDate, 'short')} - {item.current ? 'Present' : formatDate(item.endDate, 'short')}
              </span>
            </div>
            <p className="mb-1 font-semibold text-slate-700" style={{ fontSize: `${0.6875 * bScale}rem` }}>{item.company}{item.location && ` | ${item.location}`}</p>
            <Description text={item.description} bScale={bScale} />
          </div>
        </article>
      ))}
    </div>
  );
}

function EducationList({ data, compact = false, bScale = 1 }: { data: ResumeData; compact?: boolean; bScale?: number }) {
  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'}>
      {data.education.map((item) => (
        <article key={item.id}>
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="text-xs font-bold" style={{ fontSize: `${0.75 * bScale}rem` }}>{item.school}</h3>
            <span className="shrink-0 text-slate-500" style={{ fontSize: `${0.625 * bScale}rem` }}>
              {formatDate(item.startDate, 'short')} - {formatDate(item.endDate, 'short')}
            </span>
          </div>
          <p style={{ fontSize: `${0.6875 * bScale}rem` }}>{item.degree}{item.field && `, ${item.field}`}{item.location && ` | ${item.location}`}</p>
          {item.description && <Description text={item.description} className="mt-0.5 text-slate-600" bScale={bScale} />}
        </article>
      ))}
    </div>
  );
}

function SkillsList({ data, style = 'line', bScale = 1 }: { data: ResumeData; style?: 'line' | 'chips' | 'levels'; bScale?: number }) {
  if (style === 'line') return <p className="leading-5" style={{ fontSize: `${0.6875 * bScale}rem` }}>{data.skills.map((skill) => skill.name).join(' | ')}</p>;
  return (
    <div className={style === 'chips' ? 'flex flex-wrap gap-1.5' : 'space-y-1'}>
      {data.skills.map((skill) => style === 'chips' ? (
        <span key={skill.id} className="border border-slate-300 bg-white px-2 py-1 font-semibold" style={{ fontSize: `${0.625 * bScale}rem` }}>{skill.name}</span>
      ) : (
        <div key={skill.id} className="border-b border-slate-200 pb-1" style={{ fontSize: `${0.625 * bScale}rem` }}>
          <span className="font-semibold">{skill.name}</span>
        </div>
      ))}
    </div>
  );
}

function ProjectList({ data, grid = false, bScale = 1 }: { data: ResumeData; grid?: boolean; bScale?: number }) {
  return (
    <div className={grid ? 'grid grid-cols-2 gap-2' : 'space-y-2'}>
      {data.projects.map((item) => (
        <article key={item.id} className={grid ? 'border border-slate-200 p-2.5' : ''}>
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="text-xs font-bold" style={{ fontSize: `${0.75 * bScale}rem` }}>{item.name}</h3>
            {item.url && <span className="break-all text-slate-500" style={{ fontSize: `${0.55 * bScale}rem` }}>{item.url}</span>}
          </div>
          {item.description && <Description text={item.description} bScale={bScale} />}
          {item.technologies.length > 0 && <p className="mt-0.5 font-medium text-slate-600" style={{ fontSize: `${0.625 * bScale}rem` }}>Stack: {item.technologies.join(', ')}</p>}
        </article>
      ))}
    </div>
  );
}

function CertificationList({ data, boxed = false, bScale = 1 }: { data: ResumeData; boxed?: boolean; bScale?: number }) {
  return (
    <div className={boxed ? 'grid grid-cols-2 gap-2' : 'space-y-1'}>
      {data.certifications.map((item) => (
        <div key={item.id} className={boxed ? 'border border-slate-200 p-2' : 'leading-4'} style={{ fontSize: `${0.625 * bScale}rem` }}>
          <span className="font-bold">{item.name}</span>{item.issuer && ` | ${item.issuer}`}{item.date && ` | ${item.date}`}{item.url && <span className="block break-all text-slate-500">{item.url}</span>}
        </div>
      ))}
    </div>
  );
}

function Summary({ data, className = 'text-[11px] leading-5 text-slate-700', bScale = 1 }: { data: ResumeData; className?: string; bScale?: number }) {
  return data.personal.summary ? <p className={`${className} whitespace-pre-line`} style={{ fontSize: `${0.6875 * bScale}rem` }}>{data.personal.summary}</p> : null;
}

export function AtsCompactTemplate({ data, settings }: TemplateProps) {
  const s = mergeSettings(settings);
  const bScale = s.sizes.body / 100;
  const { personal } = data;
  return (
    <div
      className="min-h-full bg-white px-9 py-7 text-slate-950"
      style={{
        fontFamily: `${s.fonts.primary}, system-ui, sans-serif`,
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="border-b-2 border-slate-950 pb-2">
        <h1 className="font-bold uppercase tracking-wide" style={{ fontFamily: `${s.fonts.secondary}, system-ui, sans-serif`, fontSize: `${1.25 * (s.sizes.heading / 100)}rem` }}>{personal.fullName || 'Your Name'}</h1>
        <ContactLine data={data} className="mt-1 leading-4" bScale={bScale} />
      </header>
      <main className="space-y-3 pt-3">
        {personal.summary && <Section title="Summary"><Summary data={data} bScale={bScale} /></Section>}
        {data.skills.length > 0 && <Section title="Core Skills"><SkillsList data={data} bScale={bScale} /></Section>}
        {data.experience.length > 0 && <Section title="Professional Experience"><ExperienceList data={data} bScale={bScale} /></Section>}
        {data.projects.length > 0 && <Section title="Projects"><ProjectList data={data} bScale={bScale} /></Section>}
        <div className="grid grid-cols-2 gap-5">
          {data.education.length > 0 && <Section title="Education"><EducationList data={data} compact bScale={bScale} /></Section>}
          {data.certifications.length > 0 && <Section title="Certifications"><CertificationList data={data} bScale={bScale} /></Section>}
        </div>
      </main>
    </div>
  );
}

export function AtsSerifTemplate({ data, settings }: TemplateProps) {
  const s = mergeSettings(settings);
  const bScale = s.sizes.body / 100;
  const { personal } = data;
  return (
    <div
      className="min-h-full bg-white px-12 py-10 text-stone-900"
      style={{
        fontFamily: `${s.fonts.primary}, Georgia, serif`,
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="text-center" style={{ textAlign: s.headerAlignment }}>
        <h1 className="font-normal tracking-wide" style={{ fontFamily: `${s.fonts.secondary}, Georgia, serif`, fontSize: `${1.875 * (s.sizes.heading / 100)}rem` }}>{personal.fullName || 'Your Name'}</h1>
        <ContactLine data={data} className="mt-2 border-y border-stone-300 py-1.5" bScale={bScale} />
      </header>
      <main className="mt-5 space-y-5">
        {personal.summary && <section><h2 className="mb-2 text-center text-xs font-bold uppercase tracking-[0.2em]">Profile</h2><Summary data={data} className="text-center text-stone-700" bScale={bScale} /></section>}
        {data.experience.length > 0 && <Section title="Experience"><ExperienceList data={data} bScale={bScale} /></Section>}
        {data.education.length > 0 && <Section title="Education"><EducationList data={data} bScale={bScale} /></Section>}
        {data.projects.length > 0 && <Section title="Selected Projects"><ProjectList data={data} bScale={bScale} /></Section>}
        {data.skills.length > 0 && <Section title="Professional Skills"><SkillsList data={data} bScale={bScale} /></Section>}
        {data.certifications.length > 0 && <Section title="Credentials"><CertificationList data={data} bScale={bScale} /></Section>}
      </main>
    </div>
  );
}

export function CorporateBlueTemplate({ data, settings }: TemplateProps) {
  const s = mergeSettings(settings);
  const bScale = s.sizes.body / 100;
  const { personal } = data;
  return (
    <div
      className="min-h-full bg-white text-slate-800"
      style={{
        fontFamily: `${s.fonts.primary}, system-ui, sans-serif`,
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="bg-blue-900 px-10 py-7 text-white" style={{ textAlign: s.headerAlignment }}>
        <h1 className="font-bold tracking-tight" style={{ fontFamily: `${s.fonts.secondary}, system-ui, sans-serif`, fontSize: `${1.875 * (s.sizes.heading / 100)}rem` }}>{personal.fullName || 'Your Name'}</h1>
        <ContactLine data={data} className="mt-2 text-blue-100" bScale={bScale} />
      </header>
      <main className="px-10 py-6">
        {personal.summary && <section className="mb-5 border-l-4 border-blue-700 bg-blue-50 p-4"><h2 className="mb-1 font-bold uppercase tracking-widest text-blue-900" style={{ fontSize: `${0.625 * (s.sizes.heading / 100)}rem` }}>Executive Profile</h2><Summary data={data} bScale={bScale} /></section>}
        <div className="grid grid-cols-[2fr_1fr] gap-7">
          <div className="space-y-5">
            {data.experience.length > 0 && <Section title="Career Experience"><ExperienceList data={data} mode="boxed" bScale={bScale} /></Section>}
            {data.projects.length > 0 && <Section title="Business and Technical Projects"><ProjectList data={data} bScale={bScale} /></Section>}
          </div>
          <aside className="space-y-5 border-l border-blue-200 pl-5">
            {data.skills.length > 0 && <Section title="Capabilities"><SkillsList data={data} style="levels" bScale={bScale} /></Section>}
            {data.education.length > 0 && <Section title="Education"><EducationList data={data} compact bScale={bScale} /></Section>}
            {data.certifications.length > 0 && <Section title="Certifications"><CertificationList data={data} bScale={bScale} /></Section>}
          </aside>
        </div>
      </main>
    </div>
  );
}

export function BoardroomTemplate({ data, settings }: TemplateProps) {
  const s = mergeSettings(settings);
  const bScale = s.sizes.body / 100;
  const { personal } = data;
  return (
    <div
      className="min-h-full bg-stone-50 px-11 py-9 text-stone-900"
      style={{
        fontFamily: `${s.fonts.primary}, system-ui, sans-serif`,
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="grid grid-cols-[3fr_2fr] gap-8 border-b-4 border-stone-800 pb-5">
        <div>
          <p className="mb-1 font-bold uppercase tracking-[0.25em] text-stone-500" style={{ fontSize: `${0.625 * bScale}rem` }}>Professional Dossier</p>
          <h1 className="font-semibold" style={{ fontFamily: `${s.fonts.secondary}, system-ui, sans-serif`, fontSize: `${1.875 * (s.sizes.heading / 100)}rem` }}>{personal.fullName || 'Your Name'}</h1>
        </div>
        <ContactLine data={data} className="self-end text-right leading-5 text-stone-600" bScale={bScale} />
      </header>
      <main className="grid grid-cols-[1fr_2.2fr] gap-8 pt-6">
        <aside className="space-y-6">
          {personal.summary && <section><h2 className="mb-2 font-bold uppercase tracking-widest" style={{ fontSize: `${0.625 * bScale}rem` }}>Leadership Profile</h2><Summary data={data} bScale={bScale} /></section>}
          {data.skills.length > 0 && <Section title="Expertise"><SkillsList data={data} style="levels" bScale={bScale} /></Section>}
          {data.education.length > 0 && <Section title="Education"><EducationList data={data} compact bScale={bScale} /></Section>}
          {data.certifications.length > 0 && <Section title="Credentials"><CertificationList data={data} bScale={bScale} /></Section>}
        </aside>
        <div className="space-y-6 border-l border-stone-300 pl-7">
          {data.experience.length > 0 && <Section title="Leadership Experience"><ExperienceList data={data} bScale={bScale} /></Section>}
          {data.projects.length > 0 && <Section title="Strategic Initiatives"><ProjectList data={data} bScale={bScale} /></Section>}
        </div>
      </main>
    </div>
  );
}

export function EngineerTemplate({ data, settings }: TemplateProps) {
  const s = mergeSettings(settings);
  const bScale = s.sizes.body / 100;
  const { personal } = data;
  return (
    <div
      className="min-h-full bg-white px-9 py-8 text-slate-800"
      style={{
        fontFamily: `${s.fonts.primary}, ui-monospace, monospace`,
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="border border-slate-800 p-4">
        <p className="uppercase tracking-widest text-slate-500" style={{ fontSize: `${0.55 * bScale}rem` }}>resume.profile</p>
        <div className="mt-1 flex items-end justify-between gap-5">
          <h1 className="font-bold" style={{ fontFamily: `${s.fonts.secondary}, ui-monospace, monospace`, fontSize: `${1.5 * (s.sizes.heading / 100)}rem` }}>{personal.fullName || 'Your Name'}</h1>
          <ContactLine data={data} className="max-w-[55%] text-right leading-4" bScale={bScale} />
        </div>
      </header>
      <main className="mt-5 space-y-5">
        {personal.summary && <section className="grid grid-cols-[36px_1fr] gap-3"><span className="font-bold text-sky-700" style={{ fontSize: `${0.75 * bScale}rem` }}>00</span><div><h2 className="mb-1 font-bold uppercase" style={{ fontSize: `${0.625 * bScale}rem` }}>Objective</h2><Summary data={data} bScale={bScale} /></div></section>}
        {data.skills.length > 0 && <section className="grid grid-cols-[36px_1fr] gap-3"><span className="font-bold text-sky-700" style={{ fontSize: `${0.75 * bScale}rem` }}>01</span><div><h2 className="mb-2 border-b border-slate-300 pb-1 font-bold uppercase" style={{ fontSize: `${0.625 * bScale}rem` }}>Technical Matrix</h2><SkillsList data={data} style="chips" bScale={bScale} /></div></section>}
        {data.experience.length > 0 && <section className="grid grid-cols-[36px_1fr] gap-3"><span className="font-bold text-sky-700" style={{ fontSize: `${0.75 * bScale}rem` }}>02</span><div><h2 className="mb-2 border-b border-slate-300 pb-1 font-bold uppercase" style={{ fontSize: `${0.625 * bScale}rem` }}>Systems Experience</h2><ExperienceList data={data} mode="numbered" bScale={bScale} /></div></section>}
        {data.projects.length > 0 && <section className="grid grid-cols-[36px_1fr] gap-3"><span className="font-bold text-sky-700" style={{ fontSize: `${0.75 * bScale}rem` }}>03</span><div><h2 className="mb-2 border-b border-slate-300 pb-1 font-bold uppercase" style={{ fontSize: `${0.625 * bScale}rem` }}>Build Log</h2><ProjectList data={data} grid bScale={bScale} /></div></section>}
        <div className="grid grid-cols-2 gap-6 pl-12">
          {data.education.length > 0 && <Section title="04 / Education"><EducationList data={data} compact bScale={bScale} /></Section>}
          {data.certifications.length > 0 && <Section title="05 / Certifications"><CertificationList data={data} bScale={bScale} /></Section>}
        </div>
      </main>
    </div>
  );
}

export function DevOpsTemplate({ data, settings }: TemplateProps) {
  const s = mergeSettings(settings);
  const bScale = s.sizes.body / 100;
  const { personal } = data;
  return (
    <div
      className="min-h-full bg-slate-50 px-10 py-8 text-slate-800"
      style={{
        fontFamily: `${s.fonts.primary}, system-ui, sans-serif`,
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="flex items-start justify-between border-b border-sky-500 pb-4">
        <div>
          <p className="font-bold uppercase tracking-[0.2em] text-sky-700" style={{ fontSize: `${0.625 * bScale}rem` }}>Delivery Pipeline</p>
          <h1 className="mt-1 font-bold" style={{ fontFamily: `${s.fonts.secondary}, system-ui, sans-serif`, fontSize: `${1.5 * (s.sizes.heading / 100)}rem` }}>{personal.fullName || 'Your Name'}</h1>
        </div>
        <ContactLine data={data} className="max-w-[50%] text-right leading-4 text-slate-600" bScale={bScale} />
      </header>
      <main className="grid grid-cols-[2fr_1fr] gap-7 pt-6">
        <div className="space-y-5">
          {personal.summary && <section className="relative border-l-2 border-sky-300 pl-5"><span className="absolute -left-[5px] top-1 h-2 w-2 rounded-full bg-sky-600" /><h2 className="mb-1 font-bold uppercase tracking-widest text-sky-800" style={{ fontSize: `${0.625 * bScale}rem` }}>Plan</h2><Summary data={data} bScale={bScale} /></section>}
          {data.experience.length > 0 && <section><h2 className="mb-3 font-bold uppercase tracking-widest text-sky-800" style={{ fontSize: `${0.625 * bScale}rem` }}>Deploy / Experience</h2><ExperienceList data={data} mode="timeline" bScale={bScale} /></section>}
          {data.projects.length > 0 && <section><h2 className="mb-3 font-bold uppercase tracking-widest text-sky-800" style={{ fontSize: `${0.625 * bScale}rem` }}>Operate / Projects</h2><ProjectList data={data} grid bScale={bScale} /></section>}
        </div>
        <aside className="space-y-5 rounded-sm border border-slate-200 bg-white p-4">
          {data.skills.length > 0 && <Section title="Toolchain"><SkillsList data={data} style="levels" bScale={bScale} /></Section>}
          {data.certifications.length > 0 && <Section title="Validated"><CertificationList data={data} bScale={bScale} /></Section>}
          {data.education.length > 0 && <Section title="Foundation"><EducationList data={data} compact bScale={bScale} /></Section>}
        </aside>
      </main>
    </div>
  );
}

export function CybersecurityTemplate({ data, settings }: TemplateProps) {
  const s = mergeSettings(settings);
  const bScale = s.sizes.body / 100;
  const { personal } = data;
  return (
    <div
      className="min-h-full bg-white text-slate-900"
      style={{
        fontFamily: `${s.fonts.primary}, ui-monospace, monospace`,
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="bg-slate-950 px-10 py-7 text-white" style={{ textAlign: s.headerAlignment }}>
        <p className="uppercase tracking-[0.3em] text-emerald-400" style={{ fontSize: `${0.55 * bScale}rem` }}>Verified Professional Profile</p>
        <h1 className="mt-2 font-bold" style={{ fontFamily: `${s.fonts.secondary}, ui-monospace, monospace`, fontSize: `${1.875 * (s.sizes.heading / 100)}rem` }}>{personal.fullName || 'Your Name'}</h1>
        <ContactLine data={data} className="mt-2 leading-4 text-slate-300" bScale={bScale} />
      </header>
      <main className="px-10 py-6">
        {personal.summary && <section className="mb-5 border-l-4 border-emerald-500 bg-slate-50 p-3"><h2 className="mb-1 font-bold uppercase text-emerald-700" style={{ fontSize: `${0.625 * bScale}rem` }}>Security Profile</h2><Summary data={data} bScale={bScale} /></section>}
        <div className="grid grid-cols-[1.8fr_1fr] gap-6">
          <div className="space-y-5">
            {data.experience.length > 0 && <Section title="Operational Record"><ExperienceList data={data} mode="boxed" bScale={bScale} /></Section>}
            {data.projects.length > 0 && <Section title="Security Projects"><ProjectList data={data} bScale={bScale} /></Section>}
          </div>
          <aside className="space-y-5 border-l-2 border-emerald-500 pl-4">
            {data.skills.length > 0 && <Section title="Controls and Tools"><SkillsList data={data} style="levels" bScale={bScale} /></Section>}
            {data.certifications.length > 0 && <Section title="Clearances / Certs"><CertificationList data={data} boxed bScale={bScale} /></Section>}
            {data.education.length > 0 && <Section title="Education"><EducationList data={data} compact bScale={bScale} /></Section>}
          </aside>
        </div>
      </main>
    </div>
  );
}

export function NetworkTemplate({ data, settings }: TemplateProps) {
  const s = mergeSettings(settings);
  const bScale = s.sizes.body / 100;
  const { personal } = data;
  return (
    <div
      className="grid min-h-full grid-cols-[1fr_2.3fr] bg-white text-slate-800"
      style={{
        fontFamily: `${s.fonts.primary}, system-ui, sans-serif`,
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <aside className="border-r-4 border-cyan-700 bg-slate-100 px-6 py-8">
        <h1 className="font-bold leading-tight" style={{ fontFamily: `${s.fonts.secondary}, system-ui, sans-serif`, fontSize: `${1.25 * (s.sizes.heading / 100)}rem` }}>{personal.fullName || 'Your Name'}</h1>
        <ContactLine data={data} className="mt-4 break-words leading-5 text-slate-600" bScale={bScale} />
        {data.skills.length > 0 && <section className="mt-7"><h2 className="mb-3 border-b-2 border-cyan-700 pb-1 font-bold uppercase tracking-widest" style={{ fontSize: `${0.625 * bScale}rem` }}>Network Stack</h2><SkillsList data={data} style="levels" bScale={bScale} /></section>}
        {data.certifications.length > 0 && <section className="mt-7"><h2 className="mb-3 border-b-2 border-cyan-700 pb-1 font-bold uppercase tracking-widest" style={{ fontSize: `${0.625 * bScale}rem` }}>Certifications</h2><CertificationList data={data} bScale={bScale} /></section>}
      </aside>
      <main className="px-8 py-8">
        {personal.summary && <section className="mb-6 rounded-br-2xl border-l-4 border-t border-cyan-700 p-3"><h2 className="mb-1 font-bold uppercase tracking-widest text-cyan-800" style={{ fontSize: `${0.625 * bScale}rem` }}>Topology / Profile</h2><Summary data={data} bScale={bScale} /></section>}
        <div className="space-y-6">
          {data.experience.length > 0 && <section><h2 className="mb-3 border-b border-cyan-700 pb-1 font-bold uppercase tracking-widest" style={{ fontSize: `${0.6875 * bScale}rem` }}>Core Nodes / Experience</h2><ExperienceList data={data} mode="timeline" bScale={bScale} /></section>}
          {data.projects.length > 0 && <section><h2 className="mb-3 border-b border-cyan-700 pb-1 font-bold uppercase tracking-widest" style={{ fontSize: `${0.6875 * bScale}rem` }}>Connected Projects</h2><ProjectList data={data} grid bScale={bScale} /></section>}
          {data.education.length > 0 && <section><h2 className="mb-3 border-b border-cyan-700 pb-1 font-bold uppercase tracking-widest" style={{ fontSize: `${0.6875 * bScale}rem` }}>Foundation</h2><EducationList data={data} bScale={bScale} /></section>}
        </div>
      </main>
    </div>
  );
}

export function DataTemplate({ data, settings }: TemplateProps) {
  const s = mergeSettings(settings);
  const bScale = s.sizes.body / 100;
  const { personal } = data;
  return (
    <div
      className="min-h-full bg-white px-9 py-8 text-slate-800"
      style={{
        fontFamily: `${s.fonts.primary}, system-ui, sans-serif`,
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="grid grid-cols-[1fr_2fr] border-y-2 border-slate-800 py-4">
        <div>
          <p className="font-bold uppercase tracking-widest text-violet-700" style={{ fontSize: `${0.55 * bScale}rem` }}>Candidate</p>
          <h1 className="font-bold" style={{ fontFamily: `${s.fonts.secondary}, system-ui, sans-serif`, fontSize: `${1.5 * (s.sizes.heading / 100)}rem` }}>{personal.fullName || 'Your Name'}</h1>
        </div>
        <ContactLine data={data} className="self-center text-right leading-4" bScale={bScale} />
      </header>
      <main className="grid grid-cols-[1fr_2.4fr] gap-6 pt-6">
        <aside className="space-y-5">
          {personal.summary && <section><p className="mb-1 font-bold uppercase tracking-widest text-violet-700" style={{ fontSize: `${0.55 * bScale}rem` }}>01 / Abstract</p><Summary data={data} bScale={bScale} /></section>}
          {data.skills.length > 0 && <section><p className="mb-2 font-bold uppercase tracking-widest text-violet-700" style={{ fontSize: `${0.55 * bScale}rem` }}>02 / Variables</p><SkillsList data={data} style="levels" bScale={bScale} /></section>}
          {data.education.length > 0 && <section><p className="mb-2 font-bold uppercase tracking-widest text-violet-700" style={{ fontSize: `${0.55 * bScale}rem` }}>03 / Training Set</p><EducationList data={data} compact bScale={bScale} /></section>}
          {data.certifications.length > 0 && <section><p className="mb-2 font-bold uppercase tracking-widest text-violet-700" style={{ fontSize: `${0.55 * bScale}rem` }}>04 / Validation</p><CertificationList data={data} bScale={bScale} /></section>}
        </aside>
        <div className="space-y-6 border-l border-slate-300 pl-6">
          {data.experience.length > 0 && <section><div className="mb-3 flex justify-between border-b border-slate-800 pb-1 font-bold uppercase tracking-widest" style={{ fontSize: `${0.55 * bScale}rem` }}><span>05 / Observations</span><span>Experience</span></div><ExperienceList data={data} mode="boxed" bScale={bScale} /></section>}
          {data.projects.length > 0 && <section><div className="mb-3 flex justify-between border-b border-slate-800 pb-1 font-bold uppercase tracking-widest" style={{ fontSize: `${0.55 * bScale}rem` }}><span>06 / Outputs</span><span>Projects</span></div><ProjectList data={data} grid bScale={bScale} /></section>}
        </div>
      </main>
    </div>
  );
}

export function CloudTemplate({ data, settings }: TemplateProps) {
  const s = mergeSettings(settings);
  const bScale = s.sizes.body / 100;
  const { personal } = data;
  return (
    <div
      className="min-h-full bg-sky-50 p-7 text-slate-800"
      style={{
        fontFamily: `${s.fonts.primary}, system-ui, sans-serif`,
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <div className="min-h-full bg-white px-9 py-8">
        <header className="grid grid-cols-[1.3fr_1fr] gap-6 border-b border-sky-200 pb-6">
          <div>
            <p className="font-bold uppercase tracking-[0.25em] text-sky-600" style={{ fontSize: `${0.625 * bScale}rem` }}>Cloud Professional</p>
            <h1 className="mt-2 font-light text-slate-900" style={{ fontFamily: `${s.fonts.secondary}, system-ui, sans-serif`, fontSize: `${1.875 * (s.sizes.heading / 100)}rem` }}>{personal.fullName || 'Your Name'}</h1>
          </div>
          <ContactLine data={data} className="self-end text-right leading-5 text-slate-500" bScale={bScale} />
        </header>
        {personal.summary && <section className="my-6 grid grid-cols-[1fr_3fr] gap-5"><h2 className="font-bold uppercase tracking-widest text-sky-700" style={{ fontSize: `${0.625 * bScale}rem` }}>Overview</h2><Summary data={data} className="leading-5 text-slate-600" bScale={bScale} /></section>}
        <main className="grid grid-cols-[1.9fr_1fr] gap-8 border-t border-sky-100 pt-6">
          <div className="space-y-7">
            {data.experience.length > 0 && <section><h2 className="mb-3 font-bold uppercase tracking-widest text-sky-700" style={{ fontSize: `${0.75 * bScale}rem` }}>Cloud Experience</h2><ExperienceList data={data} mode="timeline" bScale={bScale} /></section>}
            {data.projects.length > 0 && <section><h2 className="mb-3 font-bold uppercase tracking-widest text-sky-700" style={{ fontSize: `${0.75 * bScale}rem` }}>Architectures and Projects</h2><ProjectList data={data} bScale={bScale} /></section>}
          </div>
          <aside className="space-y-6 bg-sky-50 p-4">
            {data.skills.length > 0 && <Section title="Platforms"><SkillsList data={data} style="chips" bScale={bScale} /></Section>}
            {data.certifications.length > 0 && <Section title="Cloud Credentials"><CertificationList data={data} bScale={bScale} /></Section>}
            {data.education.length > 0 && <Section title="Education"><EducationList data={data} compact bScale={bScale} /></Section>}
          </aside>
        </main>
      </div>
    </div>
  );
}
